'use client';
import { useSuspenseQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { When } from 'react-if';

import cn from '@/lib/clsxm';

import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import IconTextCombo from '@/components/shared/components/icon-text-combo';
import { getStoreDiscountOptions } from '@/components/shared/services/hooks';

import {
  DATA_CY_INTRODUCTION_TAB,
  DATA_CY_PRODUCTS_TAB,
  DATA_CY_STORE_DISCOUNT_TAB,
  DATA_CY_STORE_TABS,
} from '@/constant/cypress';
import localization, { pageLevelLocalization } from '@/constant/localization';
import { storePath } from '@/constant/routes';

import ContentIcon from '~/images/icons/content-icon';
import DeveloperDiscountCodesIcon from '~/images/icons/developer-discount-codes';
import EditIcon from '~/images/icons/edit';

export default function StoreTabs({ store }: { store: string }) {
  const pathname = usePathname();
  const { data: storeDiscount } = useSuspenseQuery(getStoreDiscountOptions(store));

  const { store: storeLocalization } = pageLevelLocalization;
  const activeStyle = 'border-b-[1px] border-[#FF9606] font-[700] text-[#565656]';
  const router = useRouter();

  const isActive = (href: string) => pathname === href;

  return (
    <Container
      data-cy={DATA_CY_STORE_TABS}
      className='br-[#F9FAFC] z-10 mt-6 flex flex-1 flex-row overflow-auto border-b-[1px]'
    >
      <Link
        data-cy={DATA_CY_PRODUCTS_TAB}
        prefetch={false}
        href={storePath.STORE_PRODUCT(store)}
        className={cn(
          'mr-5 ml-8 pb-2 hover:text-[#424244] focus:outline-hidden xl:mr-0',
          isActive(storePath.STORE_PRODUCT(store))
            ? activeStyle
            : 'text-sm font-bold text-[#76767C]',
        )}
      >
        <IconTextCombo
          Icon={
            <ContentIcon
              color={isActive(storePath.STORE_PRODUCT(store)) ? '#424244' : undefined}
            />
          }
          title={localization.products}
        />
      </Link>
      <Link
        data-cy={DATA_CY_INTRODUCTION_TAB}
        prefetch={false}
        href={storePath.STORE_INTRO(store)}
        className={cn(
          'ml-10 pb-2 whitespace-nowrap hover:text-[#424244] focus:outline-hidden',
          isActive(storePath.STORE_INTRO(store))
            ? activeStyle
            : 'text-sm font-bold text-[#76767C]',
        )}
      >
        <IconTextCombo
          Icon={
            <EditIcon color={isActive(storePath.STORE_INTRO(store)) ? '#424244' : undefined} />
          }
          title={storeLocalization.developer_introduction}
        />
      </Link>
      <When condition={storeDiscount?.length > 0}>
        <Button
          data-cy={DATA_CY_STORE_DISCOUNT_TAB}
          onClick={() => router.push(`/store/web/${store}/developer-discount-codes`)}
          className={cn(
            'mb-0 ml-10 flex h-full items-start rounded-none bg-transparent! pt-0 whitespace-nowrap hover:text-[#424244] focus:outline-hidden',
            isActive(storePath.STORE_DEVELOPER_DISCOUNT(store))
              ? activeStyle
              : 'text-sm font-bold text-[#76767C]',
          )}
        >
          <IconTextCombo
            Icon={
              <DeveloperDiscountCodesIcon
                color={
                  isActive(storePath.STORE_DEVELOPER_DISCOUNT(store)) ? '#424244' : undefined
                }
              />
            }
            title={storeLocalization.developerDiscountCodes}
          />
        </Button>
      </When>
    </Container>
  );
}
